import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Risk, Vulnerability } from '@rtkwlf/mr-infra-risk';
import { faClose } from '@rtkwlf/pro-solid-svg-icons/faClose';
import { useTranslation } from 'react-i18next';

import { useActionDrawer } from '@packages/components/action-drawer/action-drawer-context';
import type { SeverityStatusOption } from '@packages/fenrir/components';
import {
  Box,
  Button,
  Divider,
  LinkRouter,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  SeverityStatusChip,
  Stack,
  Typography,
} from '@packages/fenrir/components';

type CVEListProps = {
  item: Risk | Vulnerability;
};

const CVEListItem = React.memo(
  ({
    cveId,
    isFirstItem,
    severity,
  }: {
    cveId: string;
    isFirstItem: boolean;
    severity: SeverityStatusOption;
  }) => (
    <React.Fragment key={cveId}>
      <ListItem>
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <Stack direction="column">
            <ListItemButton
              component={LinkRouter}
              to={`https://nvd.nist.gov/vuln/detail/${cveId}`}
              isExternal={true}
              sx={{ px: 0 }}
            >
              <ListItemText primary={cveId} sx={{ color: 'primary.main' }} />
            </ListItemButton>
          </Stack>

          {isFirstItem && <SeverityStatusChip status={severity} />}
        </Stack>
      </ListItem>
      <Divider />
    </React.Fragment>
  ),
);

export const CVEList = ({ item }: CVEListProps): JSX.Element | null => {
  const { t } = useTranslation('riskManagement', { keyPrefix: 'risks' });
  const { t: tUi } = useTranslation('ui');

  const { closeDrawer } = useActionDrawer();
  const cveIds = item.cve?.primary
    ? [item.cve.primary.id, ...(item.cve.additional ?? [])]
    : [];

  return (
    <Box padding={4}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={4}
      >
        <Typography>{t('cveDrawerTitle')}</Typography>
        <Button
          startIcon={
            <FontAwesomeIcon icon={faClose} aria-label={tUi('close')} />
          }
          onClick={closeDrawer}
        >
          {tUi('close')}
        </Button>
      </Stack>

      <Typography variant="body2">{item.name}</Typography>

      <List disablePadding>
        {cveIds.map((cveId, idx) => (
          <CVEListItem
            cveId={cveId}
            isFirstItem={idx === 0}
            severity={item.severity}
          />
        ))}
      </List>
    </Box>
  );
};
